/**
 * The route export of `/foundations/schedules` (client rule 3, package A 2026-09-30): the
 * orchestrator's schedules for the market in the URL (`?country=`), one row per schedule, one
 * column per fact the page's table draws. With no market named, a file with no row — the market
 * is never guessed (CLAUDE.md §3.4).
 */

import { callOperation } from '@/api';
import { readSchedules } from '@/api/generated';
import { NOT_PUBLISHED, NO_PRINCIPAL, buildVersion } from '@/export';
import type { ExportCell, ExportRequest, ProvenanceFilter } from '@/export';

import type { ExportScope } from '../manifest';
import { CYCLE_KEY, readCountry, TICK_STATE_KEY } from './data-schedules';
import { exportDisplay, exportDisplayOf, ORCHESTRATOR_STATUS_KEY, wordKey } from '@/planning/words';

export const SCHEDULES_EXPORT_ID = 'foundations.schedules.table';

const missing: ExportCell = { kind: 'missing', state: 'not-reported' };
const text = (value: string | null | undefined): ExportCell => (value === null || value === undefined || value === '' ? missing : { kind: 'text', value });
const date = (value: string | null | undefined): ExportCell => (value === null || value === undefined || value === '' ? missing : { kind: 'date', iso: value });

/** A code the workbook says in words: a label cell, or missing when the report has none. */
const codeLabel = (value: string | null | undefined): ExportCell => (value === null || value === undefined || value === '' ? missing : { kind: 'label', value, code: value });

export async function schedulesExport(scope?: ExportScope): Promise<ExportRequest> {
  const country = readCountry(scope?.search ?? (typeof window === 'undefined' ? '' : window.location.search));
  const schedules = country === null ? [] : (await callOperation(readSchedules, { query: { country_key: country } })).schedules;
  const rows: ExportCell[][] = schedules.map((row) => [
    { kind: 'text', value: row.scheduleName },
    codeLabel(row.cycle),
    { kind: 'label', value: row.status, code: row.status },
    date(row.nextTickAt),
    date(row.lastTickAt),
    codeLabel(row.lastTickStatus),
    codeLabel(row.cycleTickState),
    text(row.lastRun?.outcome ?? row.lastRun?.status),
    date(row.lastRun?.finishedAt),
    text(row.lastRun?.partitionKey),
    text(row.cron),
    text(row.executionTimezone),
  ]);
  const filters: ProvenanceFilter[] = country === null ? [] : [{ field: 'country_key', operator: 'equals', value: country }];
  return {
    id: SCHEDULES_EXPORT_ID,
    titleKey: 'foundations.schedules.title',
    columns: [
      { key: 'schedule_name', headerKey: 'foundations.schedules.column.schedule', kind: 'text', descriptionKey: 'foundations.columnGuide.schedule.name' },
      // Codes in the screen's words in the workbook (usability audit E2/A14); the CSV keeps them.
      { key: 'cycle', headerKey: 'foundations.schedules.column.cycle', kind: 'label', display: exportDisplay((code) => wordKey(CYCLE_KEY, code)), descriptionKey: 'foundations.columnGuide.schedule.cycle' },
      { key: 'status', headerKey: 'foundations.schedules.column.status', kind: 'label', display: exportDisplayOf(ORCHESTRATOR_STATUS_KEY), descriptionKey: 'foundations.columnGuide.schedule.status' },
      { key: 'next_tick_at', headerKey: 'foundations.schedules.column.nextTick', kind: 'date', descriptionKey: 'foundations.columnGuide.schedule.nextTick' },
      { key: 'last_tick_at', headerKey: 'foundations.schedules.column.lastTick', kind: 'date', descriptionKey: 'foundations.columnGuide.schedule.lastTick' },
      { key: 'last_tick_status', headerKey: 'foundations.schedules.column.tickStatus', kind: 'label', display: exportDisplayOf(ORCHESTRATOR_STATUS_KEY), descriptionKey: 'foundations.columnGuide.schedule.tickStatus' },
      { key: 'cycle_tick_state', headerKey: 'foundations.schedules.column.cycleTick', kind: 'label', display: exportDisplayOf(TICK_STATE_KEY), descriptionKey: 'foundations.columnGuide.schedule.cycleTick' },
      { key: 'last_run_outcome', headerKey: 'foundations.schedules.column.lastRun', kind: 'text', descriptionKey: 'foundations.columnGuide.schedule.lastRun' },
      { key: 'last_run_finished_at', headerKey: 'foundations.schedules.column.finished', kind: 'date', descriptionKey: 'foundations.columnGuide.schedule.finished' },
      { key: 'last_run_partition', headerKey: 'foundations.schedules.column.partition', kind: 'text', descriptionKey: 'foundations.columnGuide.schedule.partition' },
      { key: 'cron', headerKey: 'foundations.schedules.column.cron', kind: 'text', descriptionKey: 'foundations.columnGuide.schedule.cron' },
      { key: 'execution_timezone', headerKey: 'foundations.schedules.column.zone', kind: 'text', descriptionKey: 'foundations.columnGuide.schedule.zone' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: {
      filters,
      hierarchyNode: NOT_PUBLISHED,
      timeWindow: NOT_PUBLISHED,
      scenario: NOT_PUBLISHED,
      planVersionId: NOT_PUBLISHED,
      measureDefinitions: [{
        measure: 'orchestrator_schedule_state',
        definition: 'Each schedule as the orchestrator last reported it for the market: its own state word, its ticks and its newest run. A stale report is state unknown, never running.',
        registerVersion: '',
      }],
      generatedAt: new Date().toISOString(),
      generatedBy: NO_PRINCIPAL,
      platformVersion: buildVersion(),
    },
    perimeter: {
      filters,
      sort: [{ key: 'schedule_name', direction: 'asc' }],
      visibleColumns: ['schedule_name', 'cycle', 'status', 'next_tick_at', 'last_tick_at', 'last_tick_status', 'cycle_tick_state', 'last_run_outcome', 'last_run_finished_at', 'last_run_partition', 'cron', 'execution_timezone'],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}
